/**
 * Cienki wrapper na fetch — wszystkie zapytania do API w jednym miejscu.
 *
 *  - Cookies same-site przez `credentials: 'include'` (sesja serwerowa).
 *  - Base URL: `VITE_API_BASE_URL` (np. '' w produkcji, gdy Traefik routuje /api).
 *  - Błędy != 2xx rzucają `ApiHttpError` z body, żeby UI mógł je pokazać.
 *  - Brak odpowiedzi (sieć, timeout) rzuca `ApiNetworkError` — „serwer niczego
 *    nie potwierdził", co dla zapisu czasu pracy znaczy coś innego niż odmowa.
 */

const BASE = import.meta.env.VITE_API_BASE_URL ?? ''

// Bez limitu zawieszone połączenie na iOS trzymało przycisk w stanie „w toku"
// bez końca. Wartość domyślna jest hojna — krótsze limity ustawiają wywołania,
// które blokują pracownika (start/koniec pracy).
const DEFAULT_TIMEOUT_MS = 20_000

export class ApiHttpError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message)
    this.name = 'ApiHttpError'
  }
}

export class ApiNetworkError extends Error {
  constructor(
    public reason: 'network' | 'timeout',
    options?: { cause?: unknown },
  ) {
    super(
      reason === 'timeout'
        ? 'Serwer nie odpowiedział na czas. Sprawdź połączenie i spróbuj ponownie.'
        : 'Brak połączenia z serwerem. Sprawdź połączenie i spróbuj ponownie.',
      options,
    )
    this.name = 'ApiNetworkError'
  }
}

type Json = Record<string, unknown> | unknown[] | null

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: Json
  signal?: AbortSignal
  headers?: Record<string, string>
  /** Limit na całe zapytanie łącznie z odczytem body. */
  timeoutMs?: number
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    signal,
    headers = {},
    timeoutMs = DEFAULT_TIMEOUT_MS,
  } = opts

  // Własny AbortController zamiast AbortSignal.timeout/any — starsze Safari
  // i Androidy ich nie mają, a pracownicy klikają z prywatnych telefonów.
  const controller = new AbortController()
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)
  const forwardAbort = () => controller.abort()
  if (signal?.aborted) controller.abort()
  else signal?.addEventListener('abort', forwardAbort, { once: true })

  // Anulowanie przez wołającego (np. TanStack) zostaje sobą — to nie awaria sieci.
  const toNetworkError = (err: unknown): unknown => {
    if (signal?.aborted && !timedOut) return err
    return new ApiNetworkError(timedOut ? 'timeout' : 'network', { cause: err })
  }

  try {
    let res: Response
    try {
      res = await fetch(`${BASE}${path}`, {
        method,
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...headers,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      })
    } catch (err) {
      throw toNetworkError(err)
    }

    const contentType = res.headers.get('content-type') ?? ''
    const isJson = contentType.includes('application/json')
    let data: unknown
    try {
      data = isJson
        ? res.status === 204
          ? null
          : await res.json()
        : await res.text()
    } catch (err) {
      // Zepsuty JSON to błąd treści, nie łączności — zostawiamy go bez zmian.
      if (err instanceof SyntaxError && !timedOut) throw err
      throw toNetworkError(err)
    }

    if (!res.ok) {
      const message = extractRawMessage(data) ?? `HTTP ${res.status}`
      throw new ApiHttpError(res.status, data, message)
    }

    // 2xx z HTML-em to nie odpowiedź API, tylko fallback SPA z nginx: w trakcie
    // restartu czas-api Traefik nie ma routera /api i wszystko trafia do
    // czas-web. Zwrócony jako dane string udawał świeży stan `/me` — „brak
    // otwartej sesji" i aktywny „Zaczynam pracę" przy otwartej sesji. Sprawdzamy
    // tylko text/html, żeby pusty 200 albo przyszły endpoint tekstowy nie
    // zamieniał się w awarię sieci.
    if (res.status !== 204 && contentType.includes('text/html')) {
      throw new ApiNetworkError('network')
    }

    return data as T
  } finally {
    clearTimeout(timer)
    signal?.removeEventListener('abort', forwardAbort)
  }
}

function extractRawMessage(data: unknown): string | null {
  if (typeof data === 'object' && data !== null) {
    const d = data as Record<string, unknown>
    if (typeof d.error === 'string') return d.error
    if (typeof d.message === 'string') return d.message
  }
  return null
}

/**
 * Polski tekst błędu pod UI. Priorytety:
 *  1. Brak odpowiedzi → PL komunikat (przeglądarka podaje „Failed to fetch" / „Load failed").
 *  2. 429 → ujednolicony PL komunikat (Fastify rate-limit zwraca EN).
 *  3. Nasze endpointy używają `{ error: "..." }` po polsku → zwracamy tę treść.
 *  4. Fallback per status (PL).
 */
export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiNetworkError) return err.message
  if (!(err instanceof ApiHttpError)) {
    return err instanceof Error ? err.message : 'Nieznany błąd'
  }

  if (err.status === 429) {
    return 'Zbyt wiele prób, spróbuj ponownie za chwilę'
  }

  const body = err.body
  if (typeof body === 'object' && body !== null) {
    const b = body as Record<string, unknown>
    // Nasz kontrakt: { error: "PL komunikat" } bez statusCode field-a
    if (typeof b.error === 'string' && !('statusCode' in b)) return b.error
  }

  switch (err.status) {
    case 400:
      return 'Nieprawidłowe dane'
    case 401:
      return 'Wymagane logowanie'
    case 403:
      return 'Brak uprawnień'
    case 404:
      return 'Nie znaleziono'
    case 500:
    case 502:
    case 503:
      return 'Błąd serwera, spróbuj za chwilę'
    default:
      return `Błąd (${err.status})`
  }
}
